import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Switch - Modern toggle switch component based on Tailwind CSS UI Blocks
 * Provides multiple layout variants inspired by Tailwind CSS designs
 */
const Switch = ({ 
  checked = false, 
  onChange, 
  disabled = false,
  size = 'default', // 'sm', 'default', 'lg'
  variant = 'simple', // 'simple', 'with-label', 'with-description', 'with-icon'
  label,
  description,
  labelPosition = 'right', // 'left', 'right'
  icon,
  name,
  className = '',
  ...props 
}) => {
  const { theme } = useTheme();

  const handleChange = (e) => {
    if (disabled) return;
    onChange && onChange(e.target.checked, e);
  };

  // Size variants based on Tailwind CSS patterns
  const sizeClasses = {
    sm: {
      track: 'h-5 w-9',
      thumb: 'h-4 w-4',
      translate: 'translate-x-4',
      focusRing: 'focus:ring-2 focus:ring-offset-2',
      text: 'text-sm'
    },
    default: {
      track: 'h-6 w-11',
      thumb: 'h-5 w-5',
      translate: 'translate-x-5',
      focusRing: 'focus:ring-2 focus:ring-offset-2',
      text: 'text-sm'
    },
    lg: {
      track: 'h-7 w-14',
      thumb: 'h-6 w-6',
      translate: 'translate-x-7',
      focusRing: 'focus:ring-2 focus:ring-offset-2',
      text: 'text-base'
    }
  };

  const sizeClass = sizeClasses[size] || sizeClasses.default;

  // Switch element based on Tailwind CSS design
  const switchElement = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        if (!disabled) {
          const syntheticEvent = { target: { checked: !checked } };
          handleChange(syntheticEvent);
        }
      }}
      className={`
        ${sizeClass.track}
        relative inline-flex flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
        transition-colors duration-200 ease-in-out
        ${sizeClass.focusRing} focus:ring-indigo-600 focus:outline-none
        ${checked 
          ? 'bg-indigo-600 hover:bg-indigo-700' 
          : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
      {...props}
    >
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={() => {}} // Handled by button click
        className="sr-only"
        tabIndex={-1}
      />
      
      {/* Thumb */}
      <span
        aria-hidden="true"
        className={`
          ${sizeClass.thumb}
          pointer-events-none inline-block rounded-full bg-white shadow transform ring-0 
          transition duration-200 ease-in-out
          ${checked ? sizeClass.translate : 'translate-x-0'}
        `}
      >
        {/* Optional icon in thumb */}
        {icon && (
          <span className={`
            absolute inset-0 flex items-center justify-center transition-opacity duration-200 ease-in-out
            ${checked ? 'opacity-100' : 'opacity-0'}
          `}>
            {icon}
          </span>
        )}
      </span>
    </button>
  );

  // Simple switch (no label)
  if (variant === 'simple' || (!label && !description)) {
    return <div className={className}>{switchElement}</div>;
  }

  // Switch with left label and description (Tailwind CSS pattern)
  if (labelPosition === 'left') {
    return (
      <div className={`flex items-start justify-between ${className}`}>
        <div className="flex flex-col">
          {label && (
            <label className={`${sizeClass.text} font-medium ${theme.text.primary} ${disabled ? 'opacity-50' : ''}`}>
              {label}
            </label>
          )}
          {description && (
            <p className={`text-sm ${theme.text.secondary} ${disabled ? 'opacity-50' : ''}`}>
              {description}
            </p>
          )}
        </div>
        <div className="ml-4 flex-shrink-0">
          {switchElement}
        </div>
      </div>
    );
  }

  // Switch with right label (default Tailwind CSS pattern)
  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <div className="flex-shrink-0">
        {switchElement}
      </div>
      <div className="flex flex-col">
        {label && (
          <label className={`${sizeClass.text} font-medium ${theme.text.primary} ${disabled ? 'opacity-50' : ''}`}>
            {label}
          </label>
        )}
        {description && (
          <p className={`text-sm ${theme.text.secondary} ${disabled ? 'opacity-50' : ''}`}>
            {description}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * SwitchGroup - For grouping multiple switches with a common label
 * Based on Tailwind CSS fieldset patterns
 */
const SwitchGroup = ({ 
  title, 
  description, 
  children, 
  className = '',
  ...props 
}) => {
  const { theme } = useTheme();

  return (
    <fieldset className={className} {...props}>
      {(title || description) && (
        <legend className="contents">
          <div className="mb-4">
            {title && (
              <h3 className={`text-base font-semibold leading-6 ${theme.text.primary}`}>
                {title}
              </h3>
            )}
            {description && (
              <p className={`mt-1 text-sm ${theme.text.secondary}`}>
                {description}
              </p>
            )}
          </div>
        </legend>
      )}
      <div className="space-y-4">
        {children}
      </div>
    </fieldset>
  );
};

export { Switch, SwitchGroup };
export default Switch;
